import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Tag } from "lucide-react";

export default function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "", discount_type: "percent", amount: 10, active: true, usage_limit: 0, valid_until: "" });

  const load = async () => {
    try { const { data } = await api.get("/promos"); setPromos(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, amount: Number(f.amount), usage_limit: Number(f.usage_limit) };
      if (!payload.valid_until) delete payload.valid_until;
      await api.post("/promos", payload);
      toast.success("Promo created"); setOpen(false);
      setF({ code: "", discount_type: "percent", amount: 10, active: true, usage_limit: 0, valid_until: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (p) => {
    try { await api.patch(`/promos/${p.id}`, { active: !p.active }); load(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete promo?")) return;
    try { await api.delete(`/promos/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Header title="Promo Codes" subtitle="Discount codes for repeat riders and corporate accounts">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="promo-add"><Plus className="w-4 h-4 mr-2" /> New code</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Create promo code</DialogTitle></DialogHeader>
            <form onSubmit={add} className="grid gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Code</Label><Input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="bg-black/30 border-white/10 mt-1 h-10 font-mono" placeholder="WELCOME10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase text-slate-400">Type</Label>
                  <Select value={f.discount_type} onValueChange={(v) => setF({ ...f, discount_type: v })}>
                    <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10 text-white">
                      <SelectItem value="percent">Percent %</SelectItem>
                      <SelectItem value="flat">Flat NZ$</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase text-slate-400">Amount</Label><Input required type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase text-slate-400">Usage limit (0=∞)</Label><Input type="number" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
                <div><Label className="text-xs uppercase text-slate-400">Valid until</Label><Input type="datetime-local" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
              </div>
              <Button type="submit" className="btn-gold">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </Header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promos.map((p) => (
          <div key={p.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`promo-card-${p.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-400/10 grid place-items-center"><Tag className="w-4 h-4 text-amber-400" /></div>
                <div>
                  <div className="font-mono text-lg font-bold text-amber-400">{p.code}</div>
                  <Badge className={`${p.active ? "bg-emerald-400/20 text-emerald-300 border-emerald-400/40" : "bg-slate-400/20 text-slate-400 border-slate-400/30"} border font-mono uppercase text-[10px] tracking-wider mt-1`}>{p.active ? "Active" : "Inactive"}</Badge>
                </div>
              </div>
              <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} />
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-2 text-sm">
              <div><div className="text-[10px] uppercase text-slate-500">Discount</div><div className="font-mono text-white mt-1">{p.discount_type === "percent" ? `${p.amount}%` : `NZ$ ${p.amount}`}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Uses</div><div className="font-mono text-white mt-1">{p.uses}{p.usage_limit ? `/${p.usage_limit}` : ""}</div></div>
            </div>
            <Button variant="outline" size="sm" onClick={() => del(p.id)} className="mt-4 w-full bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
